import { useState } from 'react';
import * as http from '../../../../../utils/http';
import { CareProfileResponse, TreatmentDocumentsResponse } from 'data-types';
import { MainTracker } from '../../../../../PixelTrackers/MainTracker';



interface UseCareProfileParams {
	country: string;
	setError: (status: number) => void;
	setLoading: (loading: boolean) => void;
}

interface LogWeightInput {
	weightKg: number;
	loggedAt?: string;
}

interface SetGoalInput {
	goalWeightKg: number;
}

export const useCareProfile = ({ country, setError, setLoading }: UseCareProfileParams) => {
	const [careProfile, setCareProfile] = useState<CareProfileResponse | null>(null);
	const [logWeightError, setLogWeightError] = useState<string | null>(null);
	const [isSubmittingWeight, setIsSubmittingWeight] = useState<boolean>(false);
	const [setGoalError, setSetGoalError] = useState<string | null>(null);
	const [isSubmittingGoal, setIsSubmittingGoal] = useState<boolean>(false);
	const [treatmentDocuments, setTreatmentDocuments] = useState<TreatmentDocumentsResponse | null>(null);
	const [isLoadingDocuments, setIsLoadingDocuments] = useState<boolean>(false);

	const fetchCareProfile = (options?: { soft?: boolean }) => {
		// soft: refresh data without full-page LoadingModal so open sheets stay mounted
		if (!options?.soft) {
			setLoading(true);
		}

		http
			.getFromServer(`${country}/account/care-profile`, [http.MIDDLEWARES.CUSTOMER_ID])
			.then((response: any) => {
				setCareProfile(response.data);
			})
			.catch((error: any) => {
				window.clientLogger.error('Error in useCareProfile: ', error);
				if (!options?.soft) {
					setError(500);
				}
			})
			.finally(() => {
				if (!options?.soft) {
					setLoading(false);
				}
			});
	};

	const logWeight = (data: LogWeightInput, onSuccess?: () => void) => {
		setIsSubmittingWeight(true);
		setLogWeightError(null);

		const oldProgress = careProfile?.weight?.progress ?? null;

		http
			.postToServer(`${country}/account/care-profile/weight`, data, [http.MIDDLEWARES.CUSTOMER_ID])
			.then((response: any) => {
				const newWeight = response.data;
				const latestPoint = newWeight?.series?.[newWeight.series.length - 1];
				MainTracker.track('custom', 'Log Weight', { weight: data.weightKg, bmi: latestPoint?.bmi ?? null });

				if (oldProgress && newWeight?.progress) {
					const oldLostPercent = oldProgress.startKg > 0 ? (oldProgress.totalLostKg / oldProgress.startKg) * 100 : 0;
					const newLostPercent = newWeight.progress.startKg > 0 ? (newWeight.progress.totalLostKg / newWeight.progress.startKg) * 100 : 0;
					[5, 10, 15].forEach((milestone) => {
						if (oldLostPercent < milestone && newLostPercent >= milestone) {
							MainTracker.track('custom', 'Weight Milestone Reached', { milestone: `${milestone}%` });
						}
					});
				}

				setCareProfile((current: any) => (current ? { ...current, weight: newWeight } : current));
				if (onSuccess) {
					onSuccess();
				}
			})
			.catch((error: any) => {
				window.clientLogger.error('Error in useCareProfile.logWeight: ', error);
				setLogWeightError(error?.response?.data?.message || null);
			})
			.finally(() => {
				setIsSubmittingWeight(false);
			});
	};

	const setGoal = (data: SetGoalInput, onSuccess?: () => void) => {
		setIsSubmittingGoal(true);
		setSetGoalError(null);

		http
			.postToServer(`${country}/account/care-profile/goal`, data, [http.MIDDLEWARES.CUSTOMER_ID])
			.then((response: any) => {
				MainTracker.track('custom', 'Set New Goal', { new_goal_weight: data.goalWeightKg });
				// Full refetch so dashboardState and care-team/plan stay in sync after a new goal.
				setCareProfile((current: any) =>
					current
						? {
								...current,
								weight: response.data,
								dashboardState: 'active',
						  }
						: current,
				);
				if (onSuccess) onSuccess();
				fetchCareProfile();
			})
			.catch((error: any) => {
				window.clientLogger.error('Error in useCareProfile.setGoal: ', error);
				setSetGoalError(error?.response?.data?.message || null);
			})
			.finally(() => {
				setIsSubmittingGoal(false);
			});
	};

	const fetchTreatmentDocuments = () => {
		setIsLoadingDocuments(true);

		http
			.getFromServer(`${country}/account/care-profile/documents`, [http.MIDDLEWARES.CUSTOMER_ID])
			.then((response: any) => {
				setTreatmentDocuments(response.data);
			})
			.catch((error: any) => {
				window.clientLogger.error('Error in useCareProfile.fetchTreatmentDocuments: ', error);
			})
			.finally(() => {
				setIsLoadingDocuments(false);
			});
	};

	return {
		careProfile,
		fetchCareProfile,
		logWeight,
		isSubmittingWeight,
		logWeightError,
		setGoal,
		isSubmittingGoal,
		setGoalError,
		treatmentDocuments,
		fetchTreatmentDocuments,
		isLoadingDocuments,
	};
};
